// ChatPanel — the left column of the studio shell.
//
// Renders the transcript and the input box. Send/stop wiring lives in
// the parent (App.tsx) so this component stays a "dumb" view that
// doesn't know about providers or the agent loop. Send is invoked
// through onSend prop.
//
// Layout:
//   ┌─────────────────────────────┐
//   │ scrolling transcript        │  ← flex-1, auto-scroll on append
//   │ ─ user / assistant bubbles  │
//   │ ─ tool_use chips            │
//   │ ─ live streamingText tail   │
//   │ ─ error banner (if any)     │
//   ├─────────────────────────────┤
//   │ textarea + send/stop button │  ← fixed bottom, ~80px tall
//   └─────────────────────────────┘
//
// Auto-scroll heuristic: stick to the bottom while the user is already
// near it; if they've scrolled up to read history, don't yank them back.
// Threshold: within 80px of the bottom counts as "near bottom".

import { useEffect, useRef, useState } from 'react';

import type { Message, AssistantContentBlock } from '../core/agent/types';
import { useTranscript } from '../state/transcript';

export interface ChatPanelProps {
  /** Called when the user submits a non-empty message. */
  onSend: (text: string) => void;
}

export function ChatPanel({ onSend }: ChatPanelProps) {
  const messages = useTranscript((s) => s.messages);
  const streamingText = useTranscript((s) => s.streamingText);
  const streaming = useTranscript((s) => s.streaming);
  const errorMessage = useTranscript((s) => s.errorMessage);
  const abort = useTranscript((s) => s.abort);

  const [draft, setDraft] = useState('');
  const scrollerRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);

  // Track whether the user is near the bottom; only auto-scroll when so.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = distanceFromBottom < 80;
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!stickToBottomRef.current) return;
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, streamingText, streaming]);

  const submit = () => {
    const text = draft.trim();
    if (!text || streaming) return;
    onSend(text);
    setDraft('');
  };

  return (
    <div className="flex h-full flex-col bg-[#0d0d12]">
      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto px-4 py-5"
      >
        {messages.length === 0 && !streamingText && (
          <p className="mt-8 text-center text-[12px] text-neutral-600">
            開始對話 — 講你想做嘅 design 即可。
          </p>
        )}
        <ul className="space-y-4">
          {messages.map((msg, i) => (
            <li key={i}>
              <MessageView message={msg} />
            </li>
          ))}
          {streaming && streamingText && (
            <li>
              <AssistantTextBubble text={streamingText} streaming />
            </li>
          )}
        </ul>
        {errorMessage && (
          <div className="mt-4 rounded-md border border-[#5a2a2a] bg-[#2a1414] px-3 py-2 text-[12px] text-[#ff8a8a]">
            {errorMessage}
          </div>
        )}
      </div>

      <div className="border-t border-[#222230] bg-[#0d0d12] p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // Enter to send; Shift+Enter for newline. Standard chat UX.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={streaming ? '生成中…' : '寫俾 agent 知你想做咩'}
            rows={2}
            disabled={streaming}
            className="flex-1 resize-none rounded-md border border-[#222230] bg-[#141419] px-3 py-2 text-[13px] text-neutral-100 placeholder:text-neutral-600 focus:border-[#8AFF45] focus:outline-none disabled:opacity-60"
          />
          {streaming ? (
            <button
              type="button"
              onClick={abort}
              className="rounded-md border border-[#5a2a2a] bg-[#2a1414] px-3 py-2 text-[12px] font-medium text-[#ff8a8a] hover:bg-[#3a1a1a]"
            >
              停止
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={!draft.trim()}
              className="rounded-md bg-[#8AFF45] px-4 py-2 text-[13px] font-medium text-[#0d0d12] disabled:cursor-not-allowed disabled:bg-[#222230] disabled:text-neutral-600"
            >
              傳送
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Message renderers ---------------------------------------------------

function MessageView({ message }: { message: Message }) {
  switch (message.role) {
    case 'system':
      // System prompts are agent-internal scaffolding; never shown.
      return null;
    case 'user':
      return <UserBubble text={message.content} />;
    case 'assistant':
      return <AssistantMessageView blocks={message.content} />;
    case 'tool_result':
      return <ToolResultChip result={message} />;
  }
}

function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[#1a1a24] px-3.5 py-2 text-[13px] leading-relaxed text-neutral-100">
        {text}
      </div>
    </div>
  );
}

function AssistantTextBubble({
  text,
  streaming,
}: {
  text: string;
  streaming?: boolean;
}) {
  return (
    <div className="flex">
      <div className="max-w-[85%] whitespace-pre-wrap text-[13px] leading-relaxed text-neutral-200">
        {text}
        {streaming && <span className="ml-0.5 inline-block animate-pulse text-[#8AFF45]">▍</span>}
      </div>
    </div>
  );
}

function AssistantMessageView({ blocks }: { blocks: AssistantContentBlock[] }) {
  return (
    <div className="space-y-2">
      {blocks.map((b, i) => {
        if (b.type === 'text') {
          return b.text ? <AssistantTextBubble key={i} text={b.text} /> : null;
        }
        return <ToolUseChip key={i} name={b.name} input={b.input} />;
      })}
    </div>
  );
}

function ToolUseChip({
  name,
  input,
}: {
  name: string;
  input: Record<string, unknown>;
}) {
  // Show a compact one-liner — tool name + the most identifying input
  // value (path is the obvious one for our file tools). Full input is
  // available on hover via title.
  const subject =
    typeof input.path === 'string'
      ? input.path
      : Object.values(input).find((v) => typeof v === 'string')?.toString().slice(0, 60) ?? '';
  return (
    <div
      className="inline-flex items-center gap-2 rounded-md border border-[#222230] bg-[#141419] px-2 py-1 text-[11px] text-neutral-400"
      title={JSON.stringify(input, null, 2)}
    >
      <span className="font-mono text-[#8AFF45]">→</span>
      <span className="font-mono text-neutral-300">{name}</span>
      {subject && <span className="text-neutral-500">{subject}</span>}
    </div>
  );
}

function ToolResultChip({ result }: { result: Extract<Message, { role: 'tool_result' }> }) {
  const truncated =
    result.content.length > 200
      ? `${result.content.slice(0, 200)}…`
      : result.content;
  return (
    <div
      className={`rounded-md border px-2 py-1 text-[11px] font-mono ${
        result.isError
          ? 'border-[#5a2a2a] bg-[#2a1414] text-[#ff8a8a]'
          : 'border-[#222230] bg-[#141419] text-neutral-500'
      }`}
      title={result.content}
    >
      {result.isError ? '⚠ ' : '✓ '}
      {truncated}
    </div>
  );
}
